import { put, call, takeLatest, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  ActivateTeamMember,
  DeactivateTeamMember,
  FetchTeamMembers,
  FetchTeamMembersCount,
  FetchTeamMember,
  UpdateTeamMemberAccess,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { callSecureApi, ApiService } from 'src/sagas/api/callApi'
import { TeamMember } from 'src/interfaces/team'

export type FetchTeamMembersPayload = {
  search?: string
  disabled?: boolean
  sort?: string
}

function* onFetchTeamMembers(action: AsyncAction<FetchTeamMembersPayload, { data: TeamMember[] }>) {
  const { search, disabled, sort } = action.payload

  try {
    const { payload }: { payload: { data: TeamMember[] } } = yield call(
      callSecureApi,
      Endpoints.FetchTeamMembers,
      {
        apiService: ApiService.Paraworks,
        query: { search, disabled, sort, limit: 10000 } as any,
      },
    )

    yield put(FetchTeamMembers.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team members'

    yield put(FetchTeamMembers.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ActivateTeamMemberRequest {
  teamMemberId: string
  partnerId: string
}

function* onActivateTeamMember(action: AsyncAction<ActivateTeamMemberRequest>) {
  const { teamMemberId, partnerId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.ActivateTeamMember,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: { userId: teamMemberId, partnerId },
      },
    )

    yield put(ActivateTeamMember.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to activate user'

    yield put(ActivateTeamMember.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeactivateTeamMemberRequest {
  teamMemberId: string
  partnerId: string
}

function* onDeactivateTeamMember(action: AsyncAction<DeactivateTeamMemberRequest>) {
  const { teamMemberId, partnerId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.DeactivateTeamMember,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: { userId: teamMemberId, partnerId },
      },
    )

    yield put(DeactivateTeamMember.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to deactivate user'

    yield put(DeactivateTeamMember.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchTeamMembersCount(action: AsyncAction<null, number>) {
  try {
    const { payload }: { payload: number } = yield call(
      callSecureApi,
      Endpoints.FetchTeamMembersCount,
      { apiService: ApiService.Paraworks },
    )

    yield put(FetchTeamMembersCount.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team members count'

    yield put(FetchTeamMembersCount.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchTeamMemberRequest {
  teamMemberId: string
}

function* onFetchTeamMember(action: AsyncAction<FetchTeamMemberRequest>) {
  const { teamMemberId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.FetchTeamMember,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        params: [{ field: ':teamMemberId', value: teamMemberId }],
      },
    )

    yield put(FetchTeamMember.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team member'

    yield put(FetchTeamMember.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateTeamMemberAccessRequest {
  teamMemberId: string
  teamRoleId?: string
  allMarkets?: boolean
  marketIds?: string[]
}

function* onUpdateTeamMemberAccess(action: AsyncAction<UpdateTeamMemberAccessRequest>) {
  const { teamRoleId, allMarkets, marketIds, teamMemberId } = action.payload

  try {
    const { payload }: { payload: null } = yield call(
      callSecureApi,
      Endpoints.UpdateTeamMemberAccess,
      {
        method: 'PATCH',
        apiService: ApiService.Paraworks,
        body: { roleId: teamRoleId, allMarkets, marketIds },
        params: [{ field: ':teamMemberId', value: teamMemberId }],
      },
    )

    yield put(UpdateTeamMemberAccess.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update team member access'

    yield put(UpdateTeamMemberAccess.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* teamMembersSaga() {
  yield takeLatest(FetchTeamMembers.type.REQUEST, onFetchTeamMembers)
  yield takeEvery(ActivateTeamMember.type.REQUEST, onActivateTeamMember)
  yield takeEvery(DeactivateTeamMember.type.REQUEST, onDeactivateTeamMember)
  yield takeEvery(FetchTeamMembersCount.type.REQUEST, onFetchTeamMembersCount)
  yield takeEvery(FetchTeamMember.type.REQUEST, onFetchTeamMember)
  yield takeEvery(UpdateTeamMemberAccess.type.REQUEST, onUpdateTeamMemberAccess)
}
